import { useEffect, useState } from 'react';

import { Delete02Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { MESSAGE_STATE_ERROR } from 'constants/message-app-state-contants';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Alert from 'components/Alert/Alert';
import { List } from 'components/List/List';

import {
  addBrandUpload,
  createBrand,
  deleteBrand,
  getBrandUploads,
  getBrands,
  removeBrandUpload,
  selectBrands,
  updateBrand,
} from 'api/brands-slice';
import { getCompany, selectCompany } from 'api/company-slice';

import { BrandingEdit } from './BrandingEdit/BrandingEdit';

export const Branding = ({ user, messagePopUp = () => {}, setLoading = () => {} }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const brands = useSelector(selectBrands);
  const company = useSelector(selectCompany);

  const brandIdParam = searchParams.get('brandId');

  const onGetBrands = () => dispatch(getBrands());
  const onCreateBrand = name => dispatch(createBrand(name));
  const onBrandUpdate = (id, data) => dispatch(updateBrand({ id, data }));
  const onDeleteBrand = id => dispatch(deleteBrand(id));
  const onGetBrandUploads = id => dispatch(getBrandUploads(id));
  const onRemoveBrandUpload = (brandId, uploadId) =>
    dispatch(removeBrandUpload({ brandId, uploadId }));
  const onAddBrandUpload = (brandId, upload) => dispatch(addBrandUpload({ brandId, upload }));

  const [brandId, setBrandId] = useState();
  const [brandToDelete, setBrandToDelete] = useState(null);

  useEffect(() => {
    setLoading(true);
    onGetBrands().then(_ => setLoading(false));
  }, []);

  useEffect(() => {
    if (!company?.id && user?.company_id) {
      dispatch(getCompany(user.company_id));
    }
  }, [company, user, dispatch]);

  useEffect(() => {
    if (brandIdParam) {
      setBrandId(parseInt(brandIdParam));
    }
  }, [brandIdParam]);

  const createNewBrand = async () => {
    setLoading(true);
    const { payload: newBrand } = await onCreateBrand('New Brand');
    setBrandId(newBrand.id);
    setLoading(false);
  };

  const handleUpdate = async data => {
    setLoading(true);
    const brand = brands.find(brand => brand.id === brandId);
    const signatureUpdated =
      data.brand_email &&
      (brand.brand_email !== data.brand_email || brand.brand_name !== data.brand_name);
    data = { ...data, signature_updated: signatureUpdated };
    const { payload: response } = await onBrandUpdate(brand.id, data);

    if (response.error) messagePopUp(response.error.message, MESSAGE_STATE_ERROR);
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    setBrandId(null);
    await onDeleteBrand(brandToDelete);
    setBrandToDelete(null);
    setLoading(false);
  };

  const onChangeDefault = async id => {
    setLoading(true);
    const { payload: response } = await onBrandUpdate(id, { default: true });
    if (response.error) messagePopUp(response.error.message, MESSAGE_STATE_ERROR);

    setBrands(
      brands.map(brand => {
        brand.default = brand.id === id;
        return brand;
      }),
    );
    setLoading(false);
  };

  return (
    <div className={cn('u-flex-box', 'u-flex-align-start')}>
      <List
        title="Branding"
        subtitle="Create and manage your companies branding, which is used when interacting with candidates and references via Checkmate."
        className="u-margin-right--large u-flex-align-self-normal"
        onNew={createNewBrand}>
        <List.Category title="Brands">
          {brands.map(brand => (
            <List.Item key={brand.id} title={brand.name} value={brand.id} onClick={setBrandId} />
          ))}
        </List.Category>
      </List>
      {brandId && (
        <BrandingEdit
          hasVolunteer={company?.use_volunteers}
          user={user}
          brand={brands.find(brand => brand.id === brandId)}
          onClose={() => setBrandId(null)}
          setLoading={setLoading}
          messagePopUp={messagePopUp}
          updateBrand={handleUpdate}
          changeDefault={onChangeDefault}
          getBrandUploads={onGetBrandUploads}
          removeBrandUpload={onRemoveBrandUpload}
          addBrandUpload={onAddBrandUpload}
          onDelete={id => setBrandToDelete(id)}
        />
      )}
      {/* Delete confirmation modal */}
      {brandToDelete && (
        <Alert
          show={!!brandToDelete}
          title="Are you sure you want to delete this brand permanently?"
          message="Deletion cannot be undone. Are you sure you want to continue?"
          ok="Delete"
          cancel="Cancel"
          icon={Delete02Icon}
          showConfirmationInput={true}
          type="error"
          onOk={handleDelete}
          onCancel={() => setFormToDelete(null)}
        />
      )}
    </div>
  );
};
